/**
 * A cache for layout thumbnails persisted to sessionStorage.
 * Keys: layoutId (string | number)
 * Values: { url: string, expiry: number }
 */

const STORAGE_KEY = 'layout_thumbnail_cache';

// Default TTL for cached URLs (30 minutes)
const DEFAULT_TTL_MS = 1000 * 60 * 30;

/**
 * Initialize cache from sessionStorage
 */
const initializeCache = () => {
  try {
    const storedCache = sessionStorage.getItem(STORAGE_KEY);
    return new Map(storedCache ? JSON.parse(storedCache) : []);
  } catch (error) {
    console.error('Failed to initialize layout thumbnail cache from sessionStorage:', error);
    return new Map();
  }
};

/**
 * Save cache to sessionStorage
 */
const saveCache = (cache) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...cache]));
  } catch (error) {
    console.error('Failed to save layout thumbnail cache to sessionStorage:', error);
  }
};

let cache = initializeCache();

export const layoutThumbnailCache = {
  /**
   * Retrieves a thumbnail URL if not expired.
   * Returns null if expired or missing.
   */
  get(id) {
    const record = cache.get(id);
    if (!record) return null;

    const now = Date.now();
    if (record.expiry <= now) {
      cache.delete(id);
      saveCache(cache);
      return null;
    }
    return record.url;
  },

  /**
   * Saves a thumbnail URL with an expiry timestamp.
   */
  set(id, url, ttlMs = DEFAULT_TTL_MS) {
    cache.set(id, {
      url,
      expiry: Date.now() + ttlMs,
    });
    saveCache(cache);
  },

  /**
   * Sets multiple thumbnails at once (more efficient for batch operations).
   */
  setMany(items, ttlMs = DEFAULT_TTL_MS) {
    const expiry = Date.now() + ttlMs;
    items.forEach(({ id, url }) => {
      if (url) {
        cache.set(id, { url, expiry });
      }
    });
    saveCache(cache);
  },

  /**
   * Returns IDs from the given list that are expired or missing from cache.
   */
  getExpiredIds(ids) {
    const now = Date.now();
    return ids.filter((id) => {
      const record = cache.get(id);
      return !record || record.expiry <= now;
    });
  },

  /**
   * Removes a specific layout thumbnail from the cache.
   */
  remove(id) {
    cache.delete(id);
    saveCache(cache);
  },

  /**
   * Clears the entire cache (useful in debugging or logout events).
   */
  clear() {
    cache.clear();
    saveCache(cache);
  },
};

export default layoutThumbnailCache;
